// Redistribution dimension — effective tax rate by decile.
//
// Two stacked charts inside one container:
//
//  1. A two-line chart of effective tax rate by decile, baseline vs
//     reform. The slope is the progressivity story; the change in
//     slope between the two lines is the reform's effect on
//     redistribution.
//  2. A small companion chart below: one bar per decile showing the
//     percentage-point change in effective rate (reform − baseline).
//     The eye reads SHAPE off the lines and MAGNITUDE off the bars.
//     Side-by-side the two carry the story far more clearly than the
//     line-chart-alone, which suffers a small-overlap legibility
//     problem at 1024px.
//
// Why this dimension uses a different chart family than the other five
// (decile bars / three-bar / stack / waterfall / density): no
// reference-class tool ships a redistribution view. CBO / Treasury /
// ITEP all ship a SINGLE-line effective rate by decile; the
// two-line-plus-companion-bar form is the next move the convention
// implies. See notes/redistribution_chart_precedents.md.

import type { EChartsOption } from "echarts";
import {
  GAIN_COLOUR,
  LOSS_COLOUR,
  dimensionByKey,
  isGoodDelta,
} from "../../lib/dimensions";
import type { DecileRecord, EngineOutput } from "../../lib/types";
import { Chart } from "../chart";
import { HeadlineMetric } from "../headline_metric";

const BASELINE_LINE_COLOUR = "#a39bbd"; // brand primary, lighter
const REFORM_LINE_COLOUR = "#5e4a97"; // brand primary
const ZERO_LINE_COLOUR = "#8a8a8d";
const NEUTRAL_BAR_COLOUR = "#c9c5d6";

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

export function EffectiveTaxRate({
  engineOutput,
  baselineOutput,
  isModified,
}: Props): JSX.Element {
  const reformRecords = engineOutput.results.aggregator;
  const baselineRecords =
    baselineOutput?.results.aggregator ?? reformRecords;

  const reformRates = reformRecords.map(effectiveRate);
  const baselineRates = baselineRecords.map(effectiveRate);
  // The tax components the rate actually sums, as a phrase — drops social
  // security while its module is unwired (zero everywhere), so the tooltip
  // doesn't name a component the rate doesn't include.
  const componentsPhrase = presentComponentsPhrase(reformRecords);
  const deltas = reformRates.map((r, i) => {
    const b = baselineRates[i];
    return r === null || b === null ? null : r - b;
  });

  // Top-vs-bottom percentage-point gap — the headline factual measure of
  // progressivity, across the deciles with a defined rate (bottom deciles with
  // non-positive income are skipped, #205). Format as percentage points.
  const gapBaseline = rateGap(baselineRates);
  const gapReform = rateGap(reformRates);

  return (
    <div
      className="beamm-effective-tax-rate"
      data-testid="effective-tax-rate"
    >
      <HeadlineMetric
        dimension={dimensionByKey.redistribution}
        label="Top-bottom decile effective tax rate gap"
        tooltip="The difference between the effective tax rate paid by households in the highest income decile and that paid by households in the lowest decile with a positive income (the bottom deciles, whose income is around zero or negative, have no defined rate). Positive values indicate that higher-income households face a higher effective tax rate, while negative values indicate a relatively heavier burden on lower-income households. Larger positive values therefore signal greater progressivity, whereas larger negative values signal greater regressivity."
        baseline={gapBaseline}
        reform={gapReform}
        isModified={isModified}
        formatter={formatPercentagePoints}
      />
      <Chart
        option={buildRateOption(
          reformRates,
          baselineRates,
          isModified,
          componentsPhrase,
        )}
        height="280px"
      />
      <Chart
        option={buildDeltaOption(deltas, isModified)}
        height="120px"
      />
    </div>
  );
}

function effectiveRate(r: DecileRecord): number | null {
  // Non-positive income (benefit-reliant bottom deciles) has no meaningful
  // effective rate — dividing by it clips into a garbage point. null renders
  // as a gap in the line instead. (#205)
  if (r.pre_tax_income_mean <= 0) return null;
  return (
    (r.income_tax_mean + r.social_security_mean + r.consumption_tax_mean) /
    r.pre_tax_income_mean
  );
}

// The effective-rate gap between the highest and lowest deciles that HAVE a
// defined rate. Deciles with non-positive income are null (#205) and skipped,
// so this is the top-vs-bottom gap across the positive-income range.
function rateGap(rates: (number | null)[]): number {
  const defined = rates.filter((r): r is number => r !== null);
  if (defined.length < 2) {
    return 0;
  }
  return defined[defined.length - 1] - defined[0];
}

// The tax components in the effective rate, in summation order.
const TAX_COMPONENTS: { key: keyof DecileRecord; label: string }[] = [
  { key: "income_tax_mean", label: "income tax" },
  { key: "social_security_mean", label: "social security" },
  { key: "consumption_tax_mean", label: "consumption tax" },
];

// A readable phrase of the components present in the data ("income tax and
// consumption tax"), dropping any that are zero across every decile.
function presentComponentsPhrase(records: DecileRecord[]): string {
  const labels = TAX_COMPONENTS.filter((c) =>
    records.some((r) => r[c.key] !== 0),
  ).map((c) => c.label);
  if (labels.length <= 1) {
    return labels.join("");
  }
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

function formatPercentagePoints(value: number): string {
  // Effective rate values are fractions 0..~0.5. Render as pp by
  // multiplying *100 and appending "pp" (the HeadlineMetric formats
  // the percent-change delta separately).
  return `${(value * 100).toFixed(1)}pp`;
}

function buildRateOption(
  reformRates: (number | null)[],
  baselineRates: (number | null)[],
  isModified: boolean,
  componentsPhrase: string,
): EChartsOption {
  const deciles = reformRates.map((_, i) => String(i + 1));

  return {
    grid: { left: "12%", right: "5%", top: "10%", bottom: "18%" },
    legend: {
      // Top-right so the legend doesn't collide with the x-axis name.
      data: ["Baseline", "Reform"],
      right: 0,
      top: 0,
      icon: "rect",
      itemWidth: 14,
      itemHeight: 3,
      textStyle: { fontSize: 12, color: "#3a3a3c" },
    },
    xAxis: {
      type: "category",
      name: "Decile",
      nameLocation: "middle",
      nameGap: 28,
      data: deciles,
    },
    yAxis: {
      type: "value",
      name: "Effective tax rate",
      axisLabel: {
        formatter: (v: number) => `${(v * 100).toFixed(0)}%`,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#ebe7f1" } },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      // Keep the tooltip inside the chart's own box so it isn't clipped
      // by the embedding iframe edge (#248 item 13).
      confine: true,
      extraCssText: "max-width: 320px; white-space: normal;",
      formatter: (rawParams: unknown) => {
        const params = rawParams as Array<{ axisValue: string }>;
        const decile = params[0].axisValue;
        const idx = Number(decile) - 1;
        const baseline = baselineRates[idx];
        const reform = reformRates[idx];
        if (baseline === null || reform === null) {
          return `<strong>Decile ${decile}</strong><br/>Income is around zero or negative here, so an effective rate isn't defined.`;
        }
        const delta = reform - baseline;
        const sign = delta > 0 ? "+" : delta < 0 ? "−" : "";
        const deltaText = isModified
          ? `${sign}${Math.abs(delta * 100).toFixed(2)}pp`
          : "baseline";
        return [
          `<strong>Decile ${decile}</strong>`,
          `<div style="font-size: 0.85em; color: #6e6e70; margin: 0.25rem 0;">Effective tax rate: combined ${componentsPhrase} as a share of pre-tax income.</div>`,
          `baseline: ${(baseline * 100).toFixed(1)}%`,
          `reform: ${(reform * 100).toFixed(1)}%`,
          `change: ${deltaText}`,
        ].join("<br/>");
      },
    },
    series: [
      {
        name: "Baseline",
        type: "line",
        data: baselineRates,
        symbol: "circle",
        symbolSize: 6,
        lineStyle: { color: BASELINE_LINE_COLOUR, width: 1.5 },
        itemStyle: { color: BASELINE_LINE_COLOUR },
        smooth: false,
        z: 1,
      },
      {
        name: "Reform",
        type: "line",
        data: reformRates,
        symbol: "circle",
        symbolSize: 7,
        lineStyle: { color: REFORM_LINE_COLOUR, width: 3 },
        itemStyle: { color: REFORM_LINE_COLOUR },
        smooth: false,
        z: 2,
        // Zero reference line separating net taxpayers from net
        // beneficiaries. Sits across the chart; rendered via markLine
        // on the reform series so it co-exists with the bar tooltip.
        markLine: {
          silent: true,
          symbol: "none",
          label: { show: false },
          data: [
            {
              yAxis: 0,
              lineStyle: {
                color: ZERO_LINE_COLOUR,
                width: 1,
                type: "solid",
              },
            },
          ],
        },
      },
    ],
  };
}

function buildDeltaOption(
  deltas: (number | null)[],
  isModified: boolean,
): EChartsOption {
  const deciles = deltas.map((_, i) => String(i + 1));
  const data = deltas.map((d) => {
    if (d === null) {
      // Non-positive-income decile: no defined rate, so no delta bar (#205).
      return { value: null, itemStyle: { color: NEUTRAL_BAR_COLOUR } };
    }
    // Direction-coloured per Redistribution's goodWhen: "increasing".
    // A wider top-vs-bottom rate gap = more progressive = green when
    // the rate at upper deciles is going up. We colour per-decile sign
    // here: positive delta (rate rose for this decile) reads as
    // "system more redistributive at this decile", which is the
    // dimension's "good" direction.
    const showColour = isModified && Math.abs(d) > 1e-5;
    const colour = showColour
      ? isGoodDelta(d, "increasing")
        ? GAIN_COLOUR
        : LOSS_COLOUR
      : NEUTRAL_BAR_COLOUR;
    return { value: d, itemStyle: { color: colour, borderRadius: 2 } };
  });

  // Symmetric range around zero so positive and negative deltas read
  // on the same visual scale. Floor at +/- 0.02 (2pp) so a small reform
  // still has an axis to land against.
  const maxAbs = Math.max(
    ...deltas.map((d) => (d === null ? 0 : Math.abs(d))),
    0.02,
  );
  const axisMax = Math.ceil((maxAbs * 100) / 1) / 100; // round up to nearest 1pp

  return {
    grid: { left: "12%", right: "5%", top: "10%", bottom: "30%" },
    xAxis: {
      type: "category",
      name: "Decile",
      nameLocation: "middle",
      nameGap: 22,
      data: deciles,
      axisLabel: { fontSize: 11 },
    },
    yAxis: {
      type: "value",
      name: "Δ rate (pp)",
      nameTextStyle: { fontSize: 11, color: "#6e6e70" },
      min: -axisMax,
      max: axisMax,
      axisLabel: {
        formatter: (v: number) => {
          if (Math.abs(v) < 1e-6) return "0";
          const sign = v > 0 ? "+" : "−";
          return `${sign}${(Math.abs(v) * 100).toFixed(1)}`;
        },
        fontSize: 11,
      },
      splitLine: { lineStyle: { type: "dashed", color: "#ebe7f1" } },
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      confine: true,
      formatter: (rawParams: unknown) => {
        const params = rawParams as Array<{
          axisValue: string;
          value: number | null;
        }>;
        const decile = params[0].axisValue;
        const v = params[0].value;
        if (v === null) {
          return `Decile ${decile}: rate not defined (income ≈ 0 or negative)`;
        }
        if (!isModified || Math.abs(v) < 1e-6) {
          return `Decile ${decile}: baseline`;
        }
        const sign = v > 0 ? "+" : "−";
        return `Decile ${decile}: ${sign}${(Math.abs(v) * 100).toFixed(2)}pp`;
      },
    },
    series: [
      {
        type: "bar",
        data,
        barWidth: "55%",
        markLine: {
          silent: true,
          symbol: "none",
          label: { show: false },
          data: [
            {
              yAxis: 0,
              lineStyle: {
                color: ZERO_LINE_COLOUR,
                width: 1,
                type: "solid",
              },
            },
          ],
        },
      },
    ],
  };
}
